import { BridgeClient } from 'app/service/bridge/BridgeClient';

const DATA_KEYS = [
  'api_version',
  'authentication',
  'configured',
  'enabled',
  'endpoint',
  'incremental',
];
const AUTHENTICATION_KEYS = ['header', 'query_parameter_compatibility'];
const INCREMENTAL_KEYS = ['consistency', 'periodic_full_rescan_required', 'supported'];
const TRUSTED_ERROR_CODES = new Set([
  'CREDENTIALS_REQUIRED',
  'INVALID_PAYLOAD',
  'PERSIST_FAILED',
  'UNKNOWN_ACTION',
  'UNSUPPORTED_ACTION',
]);

function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasExactKeys(value, expectedKeys) {
  return isRecord(value)
    && Object.keys(value).sort().join('\u0000') === expectedKeys.slice().sort().join('\u0000');
}

function invalidResponseError() {
  const error = new Error('The plugin returned an invalid catalog feed state.');
  error.code = 'INVALID_CATALOG_FEED_STATE';
  return error;
}

function requestError(code) {
  const error = new Error('Catalog feed request failed.');
  error.code = code;
  return error;
}

function unwrapBridgeResponse(response) {
  if (!isRecord(response) || !Object.prototype.hasOwnProperty.call(response, 'success')) {
    return response;
  }
  if (response.success !== true) throw invalidResponseError();
  const { success, ...payload } = response;
  return payload;
}

function isSafeEndpoint(endpoint) {
  if (typeof endpoint !== 'string' || endpoint.length === 0 || endpoint.length > 2048) return false;

  try {
    const absolute = /^[a-z][a-z0-9+.-]*:/i.test(endpoint);
    const parsed = new URL(endpoint, 'https://prestashop.invalid');
    if (
      (absolute && parsed.protocol !== 'https:' && parsed.protocol !== 'http:')
      || parsed.username
      || parsed.password
      || parsed.hash
    ) return false;

    const safeSegment = '(?:[a-z0-9._~-]|%[0-9a-f]{2})+';
    const friendlyFeedPath = new RegExp(
      `^/(?:${safeSegment}/)*module/[a-z0-9_-]+/feed/?$`,
      'i',
    );
    if (friendlyFeedPath.test(parsed.pathname)) {
      return parsed.search === '';
    }

    if (!/^\/(?:.*\/)?index\.php$/i.test(parsed.pathname)) return false;
    const keys = Array.from(parsed.searchParams.keys()).sort();
    return keys.join('\u0000') === ['controller', 'fc', 'module'].join('\u0000')
      && parsed.searchParams.get('fc') === 'module'
      && /^[a-z][a-z0-9_]{2,63}$/.test(parsed.searchParams.get('module') || '')
      && parsed.searchParams.get('controller') === 'feed';
  } catch (_error) {
    return false;
  }
}

export function normalizeCatalogFeedState(rawResponse) {
  const response = unwrapBridgeResponse(rawResponse);
  if (isRecord(response) && response.result === 'error') {
    const code = TRUSTED_ERROR_CODES.has(response.code) ? response.code : 'REQUEST_FAILED';
    throw requestError(code);
  }
  if (!hasExactKeys(response, ['data', 'result']) || response.result !== 'success') {
    throw invalidResponseError();
  }

  const state = response.data;
  if (
    !hasExactKeys(state, DATA_KEYS)
    || typeof state.enabled !== 'boolean'
    || typeof state.configured !== 'boolean'
    || !isSafeEndpoint(state.endpoint)
    || !Number.isInteger(state.api_version)
    || state.api_version < 3
    || !hasExactKeys(state.authentication, AUTHENTICATION_KEYS)
    || state.authentication.header !== 'X-Kea-Feed-Secret'
    || typeof state.authentication.query_parameter_compatibility !== 'boolean'
    || !hasExactKeys(state.incremental, INCREMENTAL_KEYS)
    || state.incremental.supported !== true
    || state.incremental.consistency !== 'best_effort'
    || state.incremental.periodic_full_rescan_required !== true
    || (state.enabled && !state.configured)
  ) {
    throw invalidResponseError();
  }

  return {
    apiVersion: state.api_version,
    configured: state.configured,
    enabled: state.enabled,
    endpoint: state.endpoint,
    incremental: true,
    periodicFullRescanRequired: true,
  };
}

export function createBridgeCatalogFeedApi({ client = new BridgeClient() } = {}) {
  const request = async (action, payload) => normalizeCatalogFeedState(
    await client.sendMessageWithResponse(action, payload),
  );

  return {
    getStatus: () => request('catalog.feed.status', {}),
    setEnabled: enabled => request('catalog.feed.set_enabled', { enabled }),
  };
}
